import { useMachine } from '@xstate/react';
import styled from '@emotion/styled';
import { useTranslation, useHelpHotjarUrl } from '@hotjar/translations';
import { tokens, Text, Button, Alert } from '@hotjar/hotsauce';
import { useEffect } from 'react';
import { paymentDetailsPageMachine } from './payment-details-page.machine';
import { PaymentEdit } from './components/PaymentEdit';
import { PaymentDetailsReadonly } from './components/PaymentDetailsReadonly';
import { PageSkeleton } from '../components';
import {
  ELEMENT_NAME,
  PAGE,
  PAYMENT_DETAILS_PAGE,
  trackClick,
  trackViewElement,
} from '../utils/tracking';
import type { Context } from '~/subscription-management/payment-details/payment-details-page-machine.types';
import { UpdateInvoiceDetailsBanner } from '../components/page-skeleton/UpdateInvoiceDetailsBanner';
import { SUBSCRIPTIONS } from '../constants/routes';
import { CONTACT_SUPPORT_LINK } from '../constants/links';
import NoDetailsSvg from '../resources/images/no-details.svg';
import ManuallyManagedSvg from '../resources/images/manually-managed.svg';

const S = {
  Header: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: ${tokens.spacer.x1};
  `,
};

const OverdueAlert = ({
  show = false,
  accountId,
}: Pick<Context, 'accountId'> & { show?: boolean }): JSX.Element | null => {
  const { t } = useTranslation('checkout', { keyPrefix: 'payment-details' });

  useEffect(() => {
    if (show) {
      trackViewElement({
        name: ELEMENT_NAME.paymentDetailsOverdueAlert,
        accountId,
        uiPosition: PAGE.payment,
      });
    }
  }, [accountId, show]);

  return show ? (
    <Alert kind="danger" title={t('alert-overdue-title')}>
      {t('alert-overdue-content')}
    </Alert>
  ) : null;
};

export function PaymentDetailsPage(): JSX.Element {
  const [state, send, service] = useMachine(paymentDetailsPageMachine);
  const { t } = useTranslation('checkout', { keyPrefix: 'payment-details' });
  const helpHotjarUrl = useHelpHotjarUrl();
  const { t: tPageGeneric } = useTranslation('checkout', { keyPrefix: 'page-generic' });
  const { t: tAlerts } = useTranslation('checkout', { keyPrefix: 'alerts.external-payments' });
  const { customer, isOverdue, accountId, paymentMethod, error } = state.context;
  const isExternalPaymentCustomer =
    customer?.paymentMethod.paymentMethodType === 'EXTERNAL_PAYMENT_METHOD';

  const trackPaymentsClick = (eventName: string) =>
    trackClick({
      eventName,
      uiPosition: PAGE.payment,
      accountId: state.context.accountId,
    });

  const toggleEdit = (event: PAYMENT_DETAILS_PAGE) => {
    trackPaymentsClick(event);
    send({ type: 'TOGGLE_EDIT' });
  };

  const getBlockedViewProps = () => {
    const title = isExternalPaymentCustomer ? tAlerts('title') : t('no-details-title');
    const description = isExternalPaymentCustomer
      ? tAlerts('description')
      : t('no-details-description');
    const buttonText = isExternalPaymentCustomer
      ? tAlerts('contact-support')
      : tPageGeneric('upgrade-plans');
    return {
      illustration: isExternalPaymentCustomer ? ManuallyManagedSvg : NoDetailsSvg,
      title,
      description,
      button: {
        text: buttonText,
        navigateTo: isExternalPaymentCustomer
          ? `${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`
          : SUBSCRIPTIONS,
        isExternal: isExternalPaymentCustomer,
        onClick: () => trackPaymentsClick(PAYMENT_DETAILS_PAGE.upgradePlans),
      },
    };
  };

  return (
    <PageSkeleton
      page="payment-details"
      isLoading={state.matches('initialise')}
      isBlocked={state.matches('noPaymentMethod')}
      banner={<UpdateInvoiceDetailsBanner billingAddress={customer?.billingAddress} />}
      blockedViewProps={getBlockedViewProps()}
    >
      {state.matches('viewPaymentMethod') && !isExternalPaymentCustomer && (
        <>
          <OverdueAlert show={isOverdue} accountId={accountId} />
          <S.Header>
            <Text textType="subtitle2">{t('title')}</Text>
            <Button
              as="button"
              kind="ghost_standout"
              onClick={() => toggleEdit(PAYMENT_DETAILS_PAGE.change)}
            >
              {t('change')}
            </Button>
          </S.Header>
          <PaymentDetailsReadonly paymentMethod={paymentMethod} />
        </>
      )}
      {state.matches('editPaymentMethod') && (
        <>
          {error === 'failed_submission' && <Alert kind="danger">{t('error-generic')}</Alert>}
          <S.Header>
            <Text textType="subtitle2">{t('title')}</Text>
            <Button
              as="button"
              kind="ghost_standout"
              onClick={() => toggleEdit(PAYMENT_DETAILS_PAGE.cancel)}
            >
              {t('cancel')}
            </Button>
          </S.Header>

          <PaymentEdit paymentDetailsMachineService={service} />
        </>
      )}
    </PageSkeleton>
  );
}
